import { useState, useEffect } from "react";
import { Container, Section, PageHero, CtaBanner, Loader, ChevronRight } from "../components/common";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useFaqCategories, useFaqs } from "../lib/api";
import { useSeo, breadcrumbLd, faqLd } from "../lib/seo";

export default function Faq() {
  const { data: catData } = useFaqCategories();
  const [cat, setCat] = useState(null);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  // debounce the search input
  useEffect(() => {
    const t = setTimeout(() => { setSearch(input.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [input]);

  const searching = search.length > 0;
  // when searching, look across all categories
  const { data, isLoading } = useFaqs(searching ? null : cat, search || undefined, page * 30);

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.ceil(total / 30);

  useSeo({
    title: "Auto Repair FAQs | RPM Auto Repair Riverside, 1,500+ Answers",
    description: "Answers to over 1,500 common auto repair questions, brakes, engine, transmission, hybrid, diesel, warranty, financing & more, from Riverside's ASE-certified experts.",
    path: "/faq",
    jsonLd: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }]), items.length ? faqLd(items.slice(0, 12)) : null].filter(Boolean),
  });

  const selectCat = (c) => { setInput(""); setSearch(""); setCat(c); setPage(0); };

  return (
    <>
      <PageHero eyebrow="Answers You Can Trust" title="Frequently Asked Questions"
        subtitle={`Browse ${catData?.total?.toLocaleString() || "1,500+"} honest answers across ${catData?.categories?.length || 20} categories, written by our ASE Master Certified team.`}
        image="https://images.unsplash.com/photo-1666554498255-5250121b4865?w=1200&q=70"
        crumbs={[{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }]} />

      <Section className="py-12 sm:py-16">
        <Container>
          {/* Prominent search */}
          <div className="relative mx-auto max-w-2xl">
            <Search size={20} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              inputMode="search"
              placeholder="Search questions…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="faq-search"
              className="h-14 w-full rounded-full border border-white/15 bg-steel pl-12 pr-12 text-base text-white outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
            />
            {input && (
              <button onClick={() => setInput("")} aria-label="Clear search" data-testid="faq-search-clear"
                className="absolute right-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-white/10 hover:text-white">
                <X size={18} />
              </button>
            )}
          </div>

          {/* Category chips: horizontal scroll on mobile, wrap on desktop */}
          {!searching && (
            <div className="mt-6 flex snap-x gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible"
              style={{ scrollbarWidth: "none" }}>
              <Chip active={!cat} onClick={() => selectCat(null)} testid="faq-cat-all">All</Chip>
              {catData?.categories?.map((c) => (
                <Chip key={c.category} active={cat === c.category} onClick={() => selectCat(c.category)}
                  testid={`faq-cat-${c.category.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  {c.category}
                </Chip>
              ))}
            </div>
          )}

          {/* Result header */}
          <div className="mx-auto mb-6 mt-8 flex max-w-3xl items-baseline justify-between">
            <h2 className="font-display text-xl text-white sm:text-2xl">
              {searching ? `Results for “${search}”` : (cat || "All Questions")}
            </h2>
            <span className="text-sm text-muted-foreground">{total.toLocaleString()} {total === 1 ? "result" : "results"}</span>
          </div>

          <div className="mx-auto max-w-3xl">
            {isLoading ? <Loader /> : (
              <>
                <Accordion type="single" collapsible className="space-y-3" data-testid="faq-list">
                  {items.map((f, i) => (
                    <AccordionItem key={i} value={`f${i}`} className="rounded-xl border border-white/10 bg-steel px-4 sm:px-5">
                      <AccordionTrigger className="py-4 text-left text-[15px] leading-snug text-white hover:no-underline sm:text-base">{f.question}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        <p>{f.answer}</p>
                        {f.related?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {f.related.map((r) => {
                              const s = r.toLowerCase().replace(/ & /g, "-").replace(/\s+/g, "-").replace(/\//g, "-");
                              return <Link key={r} to={`/services/${s}`} className="inline-flex items-center gap-1 rounded-full border border-primary/40 px-3 py-1 text-xs text-primary hover:bg-primary/10">{r} <ChevronRight size={12} /></Link>;
                            })}
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                {items.length === 0 && (
                  <div className="rounded-xl border border-white/10 bg-steel p-8 text-center">
                    <p className="text-muted-foreground">No questions match “{search}”. Try a different word, or call us at (951) 653-4777.</p>
                  </div>
                )}
                {pages > 1 && (
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                    <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-white/15 px-4 py-2 text-sm text-white disabled:opacity-30">Prev</button>
                    <span className="px-3 text-sm text-muted-foreground">Page {page + 1} of {pages}</span>
                    <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-white/15 px-4 py-2 text-sm text-white disabled:opacity-30">Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </Container>
      </Section>
      <CtaBanner title="Still Have Questions?" text="Our team is happy to help. Give us a call or schedule a visit, no pressure, just honest answers." />
    </>
  );
}

const Chip = ({ active, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className={`shrink-0 snap-start whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${active ? "border-primary bg-primary text-white" : "border-white/15 text-muted-foreground hover:border-primary hover:text-white"}`}>
    {children}
  </button>
);
